import { Area } from 'react-easy-crop/types';

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export const canvasToImage = async (canvas: HTMLCanvasElement): Promise<HTMLImageElement> => {
  return new Promise((resolve) => {
    const dataUrl = canvas.toDataURL();
    const image = new Image();
    image.onload = () => {
      resolve(image);
    };

    image.src = dataUrl;
  });
};

export const canvasToBlob = async (canvas: HTMLCanvasElement): Promise<Blob> => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob as Blob);
    }, 'image/jpeg');
  });
};

export const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
  clampWidth?: number,
  flip = { horizontal: false, vertical: false }
): Promise<Blob | null> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  // croppedAreaPixels values are bounding box relative
  // extract the cropped image using these values
  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image at the top left corner
  ctx.putImageData(data, 0, 0);

  // if no clamp necessary, return image blob
  if (!clampWidth || pixelCrop.width <= clampWidth) {
    return canvasToBlob(canvas);
  }

  // otherwise, scale clamp width before returning
  const croppedImage = await canvasToImage(canvas);
  const scalar = clampWidth / pixelCrop.width;
  canvas.width = clampWidth;
  canvas.height = pixelCrop.height * scalar;
  ctx.drawImage(croppedImage, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas);
};

export const isTransparentPNG = async (file: File) => {
  return new Promise<boolean>((resolve) => {
    if (file.type !== 'image/png') {
      // false for non-pngs
      resolve(false);
      return;
    }

    // create image
    const img = new Image();
    img.onload = () => {
      // create canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      // scaling to 300px width
      const width = 300;
      const scalar = width / img.width;
      canvas.width = width;
      canvas.height = img.height * scalar;
      if (ctx) {
        // draw image to canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // look for transparency
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let hasAlphaPixel = false;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            hasAlphaPixel = true;
            break;
          }
        }
        URL.revokeObjectURL(img.src);
        resolve(hasAlphaPixel);
      } else {
        // fail silently if ctx is null
        resolve(false);
      }
    };
    img.src = URL.createObjectURL(file);
  });
};
